import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { animate } from "motion";
import NavbarHeader from './navbar/NavbarHeader';
import NavLinks from './navbar/NavLinks';
import UserMenu from './navbar/UserMenu';
import NotificationDropdown from './navbar/NotificationDropdown';

const Layout = ({ children }) => {
  const location = useLocation();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 992);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 992;
      setIsMobileView(mobile);
      if (!mobile) {
        setIsMobileMenuOpen(false);
        setActiveDropdown(null);
        document.body.classList.remove('menu-open');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('menu-open');
    };
  }, []);

  // Handle body className for menu state
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }
  }, [isMobileMenuOpen]);

  // Auto-close mobile menu on route change (nav item click)
  useEffect(() => {
    if (isMobileMenuOpen) {
      setIsMobileMenuOpen(false);
      setActiveDropdown(null);
    }
  }, [location.pathname]);

  const toggleSidebar = () => {
    if (isMobileView) {
      setIsMobileMenuOpen(!isMobileMenuOpen);
      if (isMobileMenuOpen) {
        setActiveDropdown(null);
      }
    } else {
      setIsSidebarCollapsed(!isSidebarCollapsed);
    }
  };

  const handleDropdownClick = (dropdownId) => {
    if (activeDropdown === dropdownId) {
      setActiveDropdown(null);
    } else {
      setActiveDropdown(dropdownId);
    }
  };

  const handleMouseEnter = () => {
    if (isSidebarCollapsed && !isMobileView) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    if (isSidebarCollapsed && !isMobileView) {
      setIsHovered(false);
    }
  };

  useEffect(() => {
    if (!isMobileView) {
      const targetWidth = (!isSidebarCollapsed || isHovered) ? '15rem' : '4.5rem';
      const targetMargin = (!isSidebarCollapsed || isHovered) ? '15rem' : '4.5rem';

      animate(".navbar-vertical", {
        width: targetWidth
      }, {
        duration: 0.3,
        easing: [0.4, 0, 0.2, 1]
      });

      animate(".page-wrapper", {
        marginLeft: targetMargin
      }, {
        duration: 0.3,
        easing: [0.4, 0, 0.2, 1]
      });

      const navElements = document.querySelectorAll('.nav-link-title, .navbar-divider');
      navElements.forEach(el => {
        animate(el, {
          opacity: (!isSidebarCollapsed || isHovered) ? 1 : 0
        }, {
          duration: 0.2,
          easing: "ease-out"
        });
      });

      if (!isSidebarCollapsed || isHovered) {
        navElements.forEach(el => {
          el.style.display = 'block';
        });
      } else {
        setTimeout(() => {
          if (!isHovered && isSidebarCollapsed) {
            navElements.forEach(el => {
              el.style.display = 'none';
            });
          }
        }, 200);
      }
    }
  }, [isSidebarCollapsed, isHovered, isMobileView]);

  return (
    <div className="page">
      {/* Desktop Sidebar */}
      {!isMobileView && (
        <aside 
          className={`navbar navbar-vertical navbar-expand-lg bg-dark navbar-dark ${isSidebarCollapsed && !isHovered ? 'collapsed' : ''}`}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <div className="container-fluid">
            <div className="navbar-brand">
              {isSidebarCollapsed && !isHovered ? (
                <img 
                  src="/static/Logo_circle.svg" 
                  alt="Logo" 
                  className="logo-collapsed"
                />
              ) : (
                <img 
                  src="/static/logo.png" 
                  alt="Logo" 
                  className="logo-expanded"
                />
              )}
            </div>
            <div className="collapse navbar-collapse" id="sidebar-menu">
              <NavLinks 
                isCollapsed={isSidebarCollapsed && !isHovered} 
                forceCloseDropdowns={isSidebarCollapsed && !isHovered}
              />
            </div>
          </div>
        </aside>
      )}

      {/* Mobile Navigation */}
      {isMobileView && (
        <>
          <nav className="mobile-navbar">
            <button className="mobile-menu-button" onClick={toggleSidebar}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                {isMobileMenuOpen ? (
                  <>
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </>
                ) : (
                  <>
                    <line x1="3" y1="12" x2="21" y2="12"></line>
                    <line x1="3" y1="6" x2="21" y2="6"></line>
                    <line x1="3" y1="18" x2="21" y2="18"></line>
                  </>
                )}
              </svg>
            </button>
            
            <div className="mobile-navbar-brand">
              <img 
                src="/static/logo.png" 
                alt="Logo"
              />
            </div>

            <div className="mobile-navbar-actions" aria-label="Account actions">
              <NotificationDropdown mobile />
              <UserMenu />
            </div>
          </nav>

          <div className={`mobile-menu ${isMobileMenuOpen ? 'show' : ''}`}>
            <div className="mobile-menu-content">
              <NavLinks 
                isMobile={true}
                activeDropdown={activeDropdown}
                onDropdownClick={handleDropdownClick}
              />
            </div>
          </div>
        </>
      )}

      {/* Main content wrapper */}
      <div className="page-wrapper">
        {!isMobileView && (
          <div className="container-fluid">
            <NavbarHeader onToggleSidebar={toggleSidebar} />
          </div>
        )}

        {/* Main content */}
        <div className="page-content">
          {children}
        </div>
      </div>
    </div>
  );
};

export default Layout;
